import {
  Block,
  BlockFooter,
  BlockTitle,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Page,
  PageContent,
  Preloader,
  Link as F7Link,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n';
import {
  useCreateManualPaymentMethod,
  useManualPaymentMethods,
  useUpdateManualPaymentMethod,
} from '../hooks/useOrderSetup';
import type { ManualPaymentMethod } from '@restaurant/api-contracts';

// A manual payment method = an offline way to pay the operator records by hand
// (bank transfer, PayPal, etc.). Each has free-text instructions the customer
// follows, plus optional labelled account details (IBAN, PayPal address…).
// Operator language only (FRONTEND_PRACTICES Rule 11) - no displayOrder shown.
//
// Slide-in editor page, not a bottom sheet (FRONTEND_PRACTICES Rule 12).

const MAX_FIELDS = 10;

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

type FieldRow = { key: string; value: string };

/** Full-screen pushed editor for a manual payment method. Loads the method by
 *  id from the cached list; `new` opens a blank form. */
export default function ManualPaymentMethodFormPage({
  f7route,
}: {
  f7route: { params: { methodId?: string } };
}) {
  const { methodId } = f7route.params;
  const isNew = !methodId || methodId === 'new';
  const t = useT();
  const { data: methods } = useManualPaymentMethods();
  const submitRef = useRef<() => void>(() => {});

  const pm = t.orderSetup.paymentMethod;
  const method = isNew ? null : (methods?.find((m) => m.id === methodId) ?? null);
  const ready = isNew || method != null;
  const back = () => f7.views.current.router.back();

  return (
    <Page name="manual-payment-method-form" pageContent={false}>
      <Navbar title={isNew ? pm.newMethod : pm.editMethod} backLink={t.back}>
        <NavRight>
          <F7Link onClick={ready ? () => submitRef.current() : undefined}>{t.saveShort}</F7Link>
        </NavRight>
      </Navbar>
      {ready ? (
        <ManualPaymentMethodContent method={method} onDone={back} submitRef={submitRef} />
      ) : (
        <PageContent>
          <Block className="text-align-center" style={{ paddingTop: 48 }}>
            <Preloader />
          </Block>
        </PageContent>
      )}
    </Page>
  );
}

function ManualPaymentMethodContent({
  method,
  onDone,
  submitRef,
}: {
  method: ManualPaymentMethod | null;
  onDone: () => void;
  submitRef: { current: () => void };
}) {
  const pm = useT().orderSetup.paymentMethod;
  const create = useCreateManualPaymentMethod();
  const update = useUpdateManualPaymentMethod();

  const [label, setLabel] = useState('');
  const [instructions, setInstructions] = useState('');
  const [fields, setFields] = useState<FieldRow[]>([]);
  const [active, setActive] = useState(true);
  // Synchronous in-flight guard so a rapid double-tap on Save can't create two
  // methods before the mutation's isPending flips.
  const submittingRef = useRef(false);

  // Populate from the loaded method (or blank for a new one). `method` is a
  // stable reference from the cached list, so this only re-runs on a real change.
  useEffect(() => {
    setLabel(method?.label ?? '');
    setInstructions(method?.instructions ?? '');
    setFields(method ? method.accountFields.map((f) => ({ ...f })) : []);
    setActive(method ? method.isActive : true);
  }, [method]);

  const setField = (i: number, patch: Partial<FieldRow>) =>
    setFields((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addField = () =>
    setFields((rows) => (rows.length >= MAX_FIELDS ? rows : [...rows, { key: '', value: '' }]));
  const removeField = (i: number) => setFields((rows) => rows.filter((_, idx) => idx !== i));

  submitRef.current = () => {
    if (submittingRef.current) return;

    const name = label.trim();
    if (!name) {
      f7.dialog.alert(pm.nameRequired);
      return;
    }
    const instr = instructions.trim();
    if (!instr) {
      f7.dialog.alert(pm.instructionsRequired);
      return;
    }
    // Drop blank rows; both halves of a kept field are required.
    const cleaned = fields
      .map((f) => ({ key: f.key.trim(), value: f.value.trim() }))
      .filter((f) => f.key || f.value);
    if (cleaned.some((f) => !f.key || !f.value)) {
      f7.dialog.alert(pm.detailsInvalid);
      return;
    }

    const data = {
      label: name,
      instructions: instr,
      accountFields: cleaned,
      isActive: active,
    };
    submittingRef.current = true;
    const onSuccess = () => {
      toast(method ? pm.updated : pm.added);
      onDone();
    };
    const onError = (e: unknown) => f7.dialog.alert(e instanceof Error ? e.message : pm.saveFailed);
    const onSettled = () => {
      submittingRef.current = false;
    };

    if (method) {
      update.mutate({ methodId: method.id, data }, { onSuccess, onError, onSettled });
    } else {
      create.mutate(data, { onSuccess, onError, onSettled });
    }
  };

  return (
    <PageContent>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListInput
          label={pm.nameLabel}
          type="text"
          placeholder={pm.namePlaceholder}
          value={label}
          outline={f7.theme !== 'ios'}
          floatingLabel={f7.theme !== 'ios'}
          onInput={(e) => setLabel((e.target as HTMLInputElement).value)}
        />
        <ListInput
          label={pm.instructionsLabel}
          type="textarea"
          placeholder={pm.instructionsPlaceholder}
          value={instructions}
          resizable
          outline={f7.theme !== 'ios'}
          floatingLabel={f7.theme !== 'ios'}
          onInput={(e) => setInstructions((e.target as HTMLTextAreaElement).value)}
        />
        <ListItem title={pm.shownAtCheckout}>
          <ToggleSwitch slot="after" checked={active} onChange={(v) => setActive(v)} />
        </ListItem>
      </List>

      {fields.map((f, i) => (
        <div key={i}>
          <BlockTitle>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ flex: 1 }}>{pm.detail(i + 1)}</span>
              <F7Link color="red" onClick={() => removeField(i)}>
                {pm.remove}
              </F7Link>
            </span>
          </BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={pm.detailLabel}
              type="text"
              placeholder={pm.detailLabelPlaceholder}
              info={pm.detailLabelInfo}
              value={f.key}
              outline={f7.theme !== 'ios'}
              floatingLabel={f7.theme !== 'ios'}
              onInput={(e) => setField(i, { key: (e.target as HTMLInputElement).value })}
            />
            <ListInput
              label={pm.detailValue}
              type="text"
              placeholder={pm.detailValuePlaceholder}
              info={pm.detailValueInfo}
              value={f.value}
              outline={f7.theme !== 'ios'}
              floatingLabel={f7.theme !== 'ios'}
              onInput={(e) => setField(i, { value: (e.target as HTMLInputElement).value })}
            />
          </List>
        </div>
      ))}
      {fields.length < MAX_FIELDS && (
        <List strongIos strongMd insetIos insetMd>
          <ListItem>
            <F7Link slot="title" onClick={addField} style={{ color: 'var(--f7-theme-color)' }}>
              {pm.addDetail}
            </F7Link>
          </ListItem>
        </List>
      )}
      <BlockFooter>{pm.detailsFooter}</BlockFooter>
    </PageContent>
  );
}
